/* global globalThis */
import { useEffect, useState } from 'react'
import { Card, CardHeader, CardDescription, CardContent } from '@/components/ui'
import { Button, Input, Label, Alert, AlertDescription } from '@/components/ui'
import { api } from '@/api/client'
import fullColorLogo from '@/assets/brand/residesk-logo-full-color.svg'
import fullWhiteLogo from '@/assets/brand/residesk-logo-full-white.svg'

/**
 * Login Page for Internal Dashboard
 *
 * Simple Google OAuth login. The actual OAuth flow is handled by the backend:
 * 1. User clicks "Sign in with Google"
 * 2. Browser redirects to /api/internal/auth/google
 * 3. Backend redirects to Google OAuth
 * 4. Google redirects back to /api/internal/auth/google/callback
 * 5. Backend creates session and redirects to /
 * 6. useAuth hook detects authenticated user
 *
 * In local Vite dev only, it first attempts the backend's dev-login bypass
 * (DEV_AUTH_BYPASS=1 + DEV_AUTH_BYPASS_EMAIL, localhost, non-production —
 * all enforced server-side); when that endpoint is disabled the normal
 * login renders as usual.
 */
export function LoginPage() {
  const [agentEmail, setAgentEmail] = useState('')
  const [agentPassword, setAgentPassword] = useState('')
  const [showAgentPassword, setShowAgentPassword] = useState(false)
  const [agentLoginError, setAgentLoginError] = useState('')
  const [isAgentLoginLoading, setIsAgentLoginLoading] = useState(false)

  useEffect(() => {
    if (!import.meta.env.DEV) return

    let cancelled = false
    fetch('/api/internal/auth/dev-login', { credentials: 'include' })
      .then((response) => {
        if (!cancelled && response.ok) globalThis.window.location.reload()
      })
      .catch(() => {})

    return () => {
      cancelled = true
    }
  }, [])

  const handleGoogleLogin = () => {
    // Redirect to backend OAuth endpoint
    // The backend handles the entire OAuth flow and redirects back
    const redirect = globalThis.window.location.pathname + globalThis.window.location.search
    globalThis.window.location.href = `/api/internal/auth/google?${new URLSearchParams({ redirect })}`
  }

  const handleAgentLogin = async (event) => {
    event.preventDefault()
    setAgentLoginError('')
    setIsAgentLoginLoading(true)

    try {
      await api.post('/auth/login', {
        email: agentEmail.trim(),
        password: agentPassword,
      })
      globalThis.window.location.reload()
    } catch (error) {
      setAgentLoginError(error.message || 'Read-only login failed')
    } finally {
      setIsAgentLoginLoading(false)
    }
  }

  return (
    <div className="residesk-auth-page flex h-dvh min-h-dvh items-stretch justify-start overflow-y-auto px-0 py-0 sm:items-center sm:justify-center sm:px-4 sm:py-12">
      <div className="residesk-auth-shell flex min-h-dvh w-screen sm:min-h-0 sm:w-full sm:max-w-md">
        <section className="residesk-auth-brand-panel" aria-label="ResiDesk">
          <div className="residesk-auth-brand-scene" aria-hidden="true">
            <div className="residesk-auth-panel-dots" />
            <div className="residesk-auth-wheel-frame">
              <div className="residesk-auth-wheel" />
            </div>
            <div className="residesk-auth-metric-card residesk-auth-metric-card-tours" />
            <div className="residesk-auth-metric-card residesk-auth-metric-card-conversations" />
          </div>

          <div className="residesk-auth-brand-logo residesk-logo-entrance">
            <img src={fullWhiteLogo} alt="ResiDesk" className="h-auto w-full" />
          </div>

          <p className="residesk-auth-brand-message">
            Relationships that drive performance. Intelligence that drives NOI.
          </p>
        </section>

        <Card className="residesk-auth-form-panel flex min-h-dvh w-full flex-col justify-center rounded-none border-0 bg-card/95 px-5 pb-[calc(1.5rem+env(safe-area-inset-bottom))] pt-[calc(1.5rem+env(safe-area-inset-top))] shadow-none dark:bg-background sm:min-h-0 sm:rounded-lg sm:border sm:border-border/40 sm:bg-card sm:p-8 sm:shadow-2xl dark:sm:border-border/60 dark:sm:bg-card">
          <div className="residesk-auth-form-content mx-auto flex w-full max-w-sm flex-1 flex-col justify-center">
            <CardHeader className="space-y-0 p-0 text-left sm:text-center">
              <div className="residesk-auth-compact-logo residesk-logo-entrance mb-10 flex justify-start sm:mx-auto sm:mb-8 sm:w-40 sm:justify-center">
                <img
                  src={fullColorLogo}
                  alt="ResiDesk"
                  className="h-10 w-auto dark:hidden sm:h-auto sm:w-full"
                />
                <img
                  src={fullWhiteLogo}
                  alt="ResiDesk"
                  className="hidden h-10 w-auto dark:block sm:h-auto sm:w-full"
                />
              </div>
              <h1 className="font-heading text-[26px] font-semibold leading-tight tracking-tight text-foreground sm:text-2xl">
                Welcome back
              </h1>
              <CardDescription className="mt-2 leading-5">
                Sign in to ResiDesk Internal with your corporate Google account
              </CardDescription>
            </CardHeader>
            <CardContent className="mt-7 space-y-5 p-0 sm:mt-8">
              <Button
                onClick={handleGoogleLogin}
                className="h-12 w-full text-base sm:h-11 sm:text-sm"
                size="lg"
              >
                <GoogleIcon className="mr-2 h-5 w-5" />
                Sign in with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>

              <form className="space-y-4" onSubmit={handleAgentLogin}>
                <div className="space-y-2.5">
                  <Label htmlFor="agent-login-email">Email</Label>
                  <Input
                    id="agent-login-email"
                    type="email"
                    className="h-12 px-4 text-base sm:h-11 sm:text-sm"
                    autoComplete="username"
                    placeholder="name@example.com"
                    value={agentEmail}
                    onChange={(event) => setAgentEmail(event.target.value)}
                    data-cy="login-email"
                    data-pii="email"
                    required
                  />
                </div>

                <div className="space-y-2.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="agent-login-password">Read-only password</Label>
                    <button
                      type="button"
                      onClick={() => setShowAgentPassword((visible) => !visible)}
                      aria-label={showAgentPassword ? 'Hide password' : 'Show password'}
                      className="min-h-8 rounded-md px-2 text-sm font-medium text-primary transition-colors duration-200 hover:bg-primary/10"
                    >
                      {showAgentPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <Input
                    id="agent-login-password"
                    type={showAgentPassword ? 'text' : 'password'}
                    className="h-12 px-4 text-base sm:h-11 sm:text-sm"
                    autoComplete="current-password"
                    placeholder="Enter the read-only password"
                    value={agentPassword}
                    onChange={(event) => setAgentPassword(event.target.value)}
                    data-cy="login-password"
                    required
                  />
                </div>

                {agentLoginError && (
                  <Alert className="border-destructive/30 bg-destructive/5 text-destructive-text">
                    <AlertDescription>{agentLoginError}</AlertDescription>
                  </Alert>
                )}

                <Button
                  type="submit"
                  className="h-12 w-full sm:h-11"
                  variant="outline"
                  data-cy="login-submit"
                  disabled={isAgentLoginLoading || !agentEmail || !agentPassword}
                >
                  {isAgentLoginLoading ? 'Signing in...' : 'Sign in (Read-only Agent)'}
                </Button>
              </form>

              <p className="text-center text-xs leading-5 text-muted-foreground">
                Google login is for corporate users. Read-only agent login is available for E2E
                automation.
              </p>
            </CardContent>
          </div>
        </Card>
      </div>
    </div>
  )
}

/**
 * Google "G" logo icon
 */
function GoogleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  )
}

export default LoginPage
